import type {
  CmsImageData,
  CmsImageSource,
} from '@maestro/commerce-blocks/components/cms-image/cms-image.types'
import type {
  ImageConfig,
  ImageSizesEntry,
  ResponsiveImageConfig,
  SrcSetEntry,
} from '@maestro/commerce-blocks/theme/theme.types'

import NextImage from 'next/image'
import React from 'react'

import { CmsImage } from '@maestro/commerce-blocks/components/cms-image'
import { theme } from '@maestro/local/theme'

export function isValidImageElement(
  image: React.JSX.Element | undefined
): boolean {
  if (!image) {
    return false
  }

  return (
    React.isValidElement(image) &&
    (image?.type === 'img' ||
      image?.type === 'picture' ||
      image?.type === NextImage ||
      image?.type === CmsImage ||
      (typeof image?.type === 'object' &&
        String(image?.type['$$typeof']) === 'Symbol(react.lazy)'))
  )
}

// Build the srcSet attribute string from an image config entry
export function buildSrcSet(
  srcSet: SrcSetEntry[] | undefined,
  buildUrl: (width: number, height: number | undefined) => string,
  size?: { width: number; height: number }
): string | undefined {
  return srcSet
    ?.map((entry) => {
      const width = typeof entry === 'number' ? entry : entry.width
      const height = typeof entry === 'number' ? undefined : entry.height
      const finalHeight =
        height ??
        (size && size.height > 0
          ? Math.round((width * size.height) / size.width)
          : undefined)
      return `${buildUrl(width, finalHeight)} ${width}w`
    })
    .join(', ')
}

// Build the sizes attribute string from an image config entry
export function buildSizes(
  sizes: ImageSizesEntry[] | undefined
): string | undefined {
  return sizes
    ?.map((entry) =>
      entry.maxWidth
        ? `(max-width: ${entry.maxWidth}) ${entry.size}`
        : entry.size
    )
    .join(', ')
}

export function scaleSrcSet(
  srcSet: SrcSetEntry[] | undefined,
  scale: number
): SrcSetEntry[] | undefined {
  if (scale <= 1) {
    return srcSet
  }
  return srcSet?.map((entry) =>
    typeof entry === 'number'
      ? Math.ceil(entry / scale)
      : { ...entry, width: Math.ceil(entry.width / scale) }
  )
}

// Divide numeric units inside `sizes` config by scale, preserving units (px, vw, %, rem...).
export function scaleSizes(
  sizes: ImageSizesEntry[] | undefined,
  scale: number
): ImageSizesEntry[] | undefined {
  if (scale <= 1) {
    return sizes
  }
  return sizes?.map((entry) => ({
    ...entry,
    size: entry.size.replace(
      /([\d.]+)([a-z%]+)/gi,
      (_, num, unit) => `${parseFloat(num) / scale}${unit}`
    ),
  }))
}

type LocalThemeImagesCmsType = Omit<
  ReturnType<typeof theme>['images']['cms'],
  'config'
>
export type ConfigName = keyof LocalThemeImagesCmsType
export type ConfigAppearance<T extends ConfigName> =
  keyof LocalThemeImagesCmsType[T]

export type CmsComponentConfig = Record<string, ResponsiveImageConfig>

export function scaleImageConfig(
  config: ResponsiveImageConfig,
  scale = 1
): ResponsiveImageConfig {
  if (scale <= 1) {
    return config
  }
  return {
    desktop: {
      ...config.desktop,
      width: Math.ceil(config.desktop.width / scale),
      sizes: scaleSizes(config.desktop.sizes, scale),
      srcSet: scaleSrcSet(config.desktop.srcSet, scale),
    },
    mobile: config.mobile,
  }
}

export function converterUrl(
  src: string,
  params: Record<string, string | number>
): string {
  const url = new URL(theme().images.converter)
  url.searchParams.set('url', src)
  url.searchParams.set('auto', 'format')
  url.searchParams.set('format', 'webp')
  for (const [k, v] of Object.entries(params)) {
    url.searchParams.set(k, `${v}`)
  }
  return url.href
}

export function isSvgUrl(url: string): boolean {
  return /\.svg(\?|#|$)/i.test(url)
}

function buildVariantFromUrl(rawUrl: string, cfg: ImageConfig): CmsImageSource {
  if (isSvgUrl(rawUrl)) {
    return {
      width: cfg.width,
      height: cfg.height,
      src: rawUrl,
    }
  }

  const { quality: defaultQuality, fit: defaultFit } = theme().images
  const buildUrl = (width: number, height: number | undefined) =>
    converterUrl(rawUrl, {
      width,
      ...(height && height > 0 ? { height } : {}),
      quality: cfg.quality ?? defaultQuality,
      fit: cfg.fit ?? defaultFit,
    })

  return {
    width: cfg.width,
    height: cfg.height,
    src: buildUrl(cfg.width, cfg.height),
    srcSet: buildSrcSet(cfg.srcSet, buildUrl, {
      width: cfg.width,
      height: cfg.height,
    }),
    sizes: buildSizes(cfg.sizes),
  }
}

export function buildPlaceholderImageData(
  config: ResponsiveImageConfig,
  placeholder: string
): CmsImageData {
  return {
    desktop: {
      width: config.desktop.width,
      height: config.desktop.height,
      src: placeholder,
    },
    mobile: {
      width: config.mobile.width,
      height: config.mobile.height,
      src: placeholder,
    },
    isPlaceholder: true,
  }
}

export function buildImageDataFromUrl(
  url: string | null | undefined,
  config: ResponsiveImageConfig,
  placeholder?: string
): CmsImageData | null {
  if (!url) {
    return placeholder ? buildPlaceholderImageData(config, placeholder) : null
  }

  return {
    desktop: buildVariantFromUrl(url, config.desktop),
    mobile: buildVariantFromUrl(url, config.mobile),
  }
}
